import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "reactstrap";
import { message, Popover, Tag, Table } from "antd";
import type { ColumnsType } from "antd/lib/table";
import exportFromJSON from "export-from-json";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
} from "../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../components/upload/shared/uploadReport";
import { useSequenceGate } from "../../../components/upload/shared/SequenceGate";
import {
  wbsUploadPreviewColumns,
  WBS_UPLOAD_TABLE_WIDTH,
} from "../../../components/upload/shared/previewColumns/wbsUploadPreviewColumns";
import { projectsApis } from "../../../api/projects";
import { tasksApis } from "../../../api/NewTasks";
import contractorsApis from "../../../api/master/contractors";
import sitebreakdownMasterApis from "../../../api/master/sitebreakdown";

// Thin wrapper over the shared ExcelUploadStageModal (upload → validating →
// preview → committing → done | failed) for Bulk WBS Creation. Replaces the
// legacy fire-and-forget BulkUpload flow for the "wbs-import" module key only
// (task-boq/budget/resource/tool stay on BulkUpload). Only the WBS-specific
// pieces live here: blob template URL, sequence gate, reference CSV exports,
// preview columns, copy strings.
//
// Note: WBS validation-with-errors lands as status 'Error' on the backend
// (unlike norms), so error rows surface in the shared modal's *failed* stage
// rather than a mixed preview — by design, no WBS-specific stages here.

const blobUrl: String = process.env.REACT_APP_BLOB_URL || "";
const blobContainer: String = process.env.REACT_APP_BLOB_CONTAINER || "";

const WBS_TEMPLATE_URL = `${blobUrl}${blobContainer}/wbs-creation-template-v2.xlsx`;

const WBS_REQUIRED_MODULES = ["Phases", "Activities"];

const WBS_REFERENCES = [
  "Tasks",
  "Project Users",
  "Work UOMs",
  "Work Categories",
  "Contractors",
  "Site Structures",
  "Locations",
];

// Copied from BulkUpload.tsx WBSDependencyReference — BulkUpload keeps its own.
const WBSDependencyReference = React.memo(() => (
  <Alert
    color="info"
    className="mt-3"
    style={{
      fontSize: "14px",
      background: "#e5ebfa",
      border: "1px solid #dbdfea",
    }}
  >
    <strong>Reference:</strong> For the <code>Dependency Type</code> column,
    use only the following options:
    <br />
    <ul className="mt-1 mb-0" style={{ paddingLeft: "20px" }}>
      <li>
        <b>FS</b> – Finish-to-Start
      </li>
      <li>
        <b>SF</b> – Start-to-Finish
      </li>
      <li>
        <b>FF</b> – Finish-to-Finish
      </li>
      <li>
        <b>SS</b> – Start-to-Start
      </li>
    </ul>
  </Alert>
));

// Reference-data block for the upload stage: the 7 master-data CSV export
// links + dependency-type legend + mandatory-fields note. Fetch functions are
// COPIED from the wbs-import branch of src/components/upload/BulkUpload.tsx
// (which keeps its own copies untouched). The component is only mounted once
// the sequence gate allows the form, so the fetches are effectively lazy —
// mirroring BulkUpload's `fetchedRefs && isUploadAllowed` behavior.
const WbsUploadIntro = ({
  projectId,
  projectSiteName,
  projectSiteId,
}: {
  projectId?: string;
  projectSiteName?: string;
  projectSiteId?: string;
}) => {
  const [projectUsers, setProjectUsers] = useState<any>([]);
  const [workUoms, setWorkUoms] = useState<any>([]);
  const [workCategories, setWorkCategories] = useState<any>([]);
  const [tasks, setTasks] = useState<any>([]);
  const [contractors, setContractors] = useState<any>([]);
  const [siteStructures, setSiteStructures] = useState<any>([]);
  const [locations, setLocations] = useState<any>([]);
  const [fetchedRefs, setFetchedRefs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // Fetch project users
  const fetchProjectUsers = async (projectId: string) => {
    try {
      const { data, status } = await projectsApis.fetchProjectUsers(projectId);
      if (status) {
        const userOptions = data.map((user: any) => ({
          UserName: user?.userName,
        }));
        setProjectUsers(userOptions);
      }
    } catch (error) {
      console.error("Error fetching project users:", error);
    }
  };

  // Fetch tasks
  const fetchTasks = async (projectId: string) => {
    try {
      setLoading(true);
      const {
        data,
        status,
        message: apiMessage,
      } = await projectsApis.fetchProjectWBS(projectId);

      if (status && data?.phases) {
        const taskOptions: any[] = [];

        // Process each phase
        data.phases.forEach((phase: any) => {
          if (phase.activities && Array.isArray(phase.activities)) {
            // Process each activity in the phase
            phase.activities.forEach((activity: any) => {
              if (activity.tasks && Array.isArray(activity.tasks)) {
                // Process each task in the activity
                activity.tasks.forEach((task: any) => {
                  taskOptions.push({
                    // Task details
                    TaskName: task.taskName,
                    TaskId: task.taskSequenceId,

                    // Activity details
                    ActivityName: activity.name,

                    // Phase details
                    PhaseName: phase.phaseName,

                    // Work/output basis
                    WorkType: task.workType,
                    WorkQty: task.workQuantity,
                    WorkUOM: task.workUom,
                    OutputQty: task.outputQuantity,
                    OutputUom: task.outputBasisUom,
                  });
                });
              }
            });
          }
        });

        setTasks(taskOptions);
      } else {
        console.error("No phases data found in response");
        messageApi.error(apiMessage || "No task data available");
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      messageApi.error("Failed to fetch tasks from server");
    } finally {
      setLoading(false);
    }
  };

  // Fetch contractors for the project's site
  const fetchContractorsList = async (siteId: string) => {
    try {
      const { data, status } = await contractorsApis.fetchContractorBySiteId({
        id: String(siteId),
      });
      if (status && Array.isArray(data)) {
        setContractors(
          data.map((item: any) => ({
            ContractorId: item?.contractorID,
            ContractorName: item?.companyName,
          }))
        );
      } else {
        setContractors([]);
      }
    } catch (error) {
      console.error("Error fetching contractors:", error);
      setContractors([]);
    }
  };

  // Fetch site structures (units + sub-units flattened) for the project's site
  const fetchSiteStructuresList = async (siteId: string) => {
    try {
      const unitsRes = await sitebreakdownMasterApis.fetchUnitsBySiteId({
        siteId,
      });
      if (!unitsRes?.status || !Array.isArray(unitsRes.data)) {
        setSiteStructures([]);
        return;
      }
      const units = unitsRes.data;
      const subUnitsResponses = await Promise.all(
        units.map((unit: any) =>
          sitebreakdownMasterApis
            .fetchSubUnitsByUnitId({ siteId, unitId: unit.id })
            .catch(() => ({ status: false, data: [] }))
        )
      );
      const rows: any[] = [];
      units.forEach((unit: any, idx: number) => {
        const subRes = subUnitsResponses[idx];
        const subUnits =
          subRes?.status && Array.isArray(subRes.data) ? subRes.data : [];
        if (subUnits.length === 0) {
          rows.push({
            SiteStructureUnitId: unit.id,
            "Tower/Villa": unit.unitName,
            SiteStructureSubUnitId: "",
            "Flat/Shop": "",
            Floor: "",
          });
        } else {
          subUnits.forEach((sub: any) => {
            rows.push({
              SiteStructureUnitId: unit.id,
              "Tower/Villa": unit.unitName,
              SiteStructureSubUnitId: sub.id,
              "Flat/Shop": sub.subUnitName || sub.unitName || "",
              Floor: sub.floorNo || "",
            });
          });
        }
      });
      setSiteStructures(rows);
    } catch (error) {
      console.error("Error fetching site structures:", error);
      setSiteStructures([]);
    }
  };

  // Fetch locations for the project's site
  const fetchLocationsList = async (siteId: string) => {
    try {
      const response = await sitebreakdownMasterApis.fetchLocationsBySiteId({
        siteId,
      });
      if (response?.status && Array.isArray(response.data)) {
        setLocations(
          response.data.map((loc: any) => ({
            LocationId: loc?.id,
            LocationName: loc?.locationName,
          }))
        );
      } else {
        setLocations([]);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      setLocations([]);
    }
  };

  // Fetch work UOMs
  const fetchWorkUomsList = async () => {
    try {
      const response = await tasksApis.workUom({});
      const { data, status } = response || {};
      if (status) {
        setWorkUoms(
          data?.map((item: any) => ({
            WorkUOMName: item.uomName,
            WorkUOMId: item.workUomId,
          })) || []
        );
      }
    } catch (error) {
      console.error("Error fetching work UOMs:", error);
      messageApi.error("Failed to fetch work UOMs");
    }
  };

  // Fetch work categories
  const fetchWorkCatgoriesList = async () => {
    try {
      const response: any = await tasksApis.fetchWorkCatgoriesList();
      const { data, status } = response || {};
      if (status) {
        const formatted = data.map(
          (item: { workCategoryName: any; workCategoryId: any }) => ({
            workCategoryName: item.workCategoryName,
            workCategoryId: item.workCategoryId,
          })
        );
        setWorkCategories(formatted);
      }
    } catch (error) {
      console.error("Error fetching work categories:", error);
      messageApi.open({
        type: "error",
        content: "Failed to fetch work categories. Please try again.",
      });
    }
  };

  useEffect(() => {
    if (fetchedRefs || !projectId) return;
    // same fetch set as BulkUpload's wbs-import branch
    fetchTasks(projectId);
    fetchProjectUsers(projectId);
    fetchWorkUomsList();
    fetchWorkCatgoriesList();
    if (projectSiteId) {
      fetchContractorsList(projectSiteId);
      fetchSiteStructuresList(projectSiteId);
      fetchLocationsList(projectSiteId);
    }
    // prevent repeat fetch on re-render (component remounts on each modal open)
    setFetchedRefs(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedRefs, projectId, projectSiteId]);

  const referenceData: { [key: string]: any } = useMemo(
    () => ({
      Tasks: tasks,
      "Project Users": projectUsers,
      "Work UOMs": workUoms,
      "Work Categories": workCategories,
      Contractors: contractors,
      "Site Structures": siteStructures,
      Locations: locations,
    }),
    [
      tasks,
      projectUsers,
      workUoms,
      workCategories,
      contractors,
      siteStructures,
      locations,
    ]
  );

  const referenceFileNames: { [key: string]: string } = useMemo(
    () => ({
      Tasks: `Tasks_Reference_${projectId || "All"}`,
      "Project Users": `ProjectUsers_${projectId || "All"}`,
      "Work UOMs": "WorkUOMs",
      "Work Categories": "WorkCategories",
      Contractors: `Contractors_${projectSiteName || projectSiteId || "All"}`,
      "Site Structures": `SiteStructures_${
        projectSiteName || projectSiteId || "All"
      }`,
      Locations: `Locations_${projectSiteName || projectSiteId || "All"}`,
    }),
    [projectId, projectSiteName, projectSiteId]
  );

  const handleExportReference = useCallback(
    (ref: string) => {
      const exportType = exportFromJSON.types.csv;
      exportFromJSON({
        data: referenceData[ref],
        fileName: referenceFileNames[ref],
        exportType,
      });
    },
    [referenceData, referenceFileNames]
  );

  const referenceLinks = useMemo(
    () =>
      WBS_REFERENCES.map((ref: string, index: number) => (
        <React.Fragment key={ref}>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleExportReference(ref);
            }}
            target="_self"
            style={{ textDecoration: "underline" }}
          >
            {ref}
          </a>
          {index < WBS_REFERENCES.length - 1 && ", "}
        </React.Fragment>
      )),
    [handleExportReference]
  );

  return (
    <>
      {contextHolder}
      <p className="mt-2 mb-0">
        You can check {referenceLinks} for reference
        {loading && <Spinner size="sm" className="ms-2" />}
      </p>
      <WBSDependencyReference />
      <p>
        <strong>Note:</strong> All fields in the template are mandatory. Each
        one must have unique information to avoid errors during the import
        process.
      </p>
    </>
  );
};

const round2 = (n: number) => Math.round(n * 100) / 100;

// Preview info chip: per-activity task-weightage totals computed from the
// uploaded rows, so weightages can be sanity-checked before committing. Shown
// on the valid preview via ExcelUploadStageModal's `summaryTags` hook.
// (Validation already blocks any activity over 100%; this also surfaces
// activities that fall *under* 100%, which validation allows.)
const WbsActivityTotals = ({ report }: { report: UploadReportState }) => {
  const totals = useMemo(() => {
    const map = new Map<
      string,
      { phase: string; activity: string; total: number; count: number }
    >();
    for (const row of report.rows || []) {
      const activity = (row?.activityName ?? "").toString();
      if (!activity) continue;
      const phase = (row?.phaseName ?? "").toString();
      const key = `${phase}||${activity}`;
      const weight = Number(row?.taskWeightage) || 0;
      const entry = map.get(key) ?? { phase, activity, total: 0, count: 0 };
      entry.total += weight;
      entry.count += 1;
      map.set(key, entry);
    }
    return Array.from(map.values()).map((e) => ({
      ...e,
      total: round2(e.total),
    }));
  }, [report.rows]);

  if (!totals.length) return null;

  const truncated = report.totalRecords > (report.rows?.length || 0);
  const anyUnder = totals.some((t) => t.total < 100);

  type TotalRow = (typeof totals)[number];
  const columns: ColumnsType<TotalRow> = [
    {
      title: "Activity",
      dataIndex: "activity",
      render: (_: unknown, t: TotalRow) => (
        <div style={{ wordBreak: "break-word" }}>
          <div style={{ fontWeight: 500 }}>{t.activity}</div>
          {t.phase ? (
            <div style={{ color: "#8094ae", fontSize: 11 }}>{t.phase}</div>
          ) : null}
        </div>
      ),
    },
    {
      title: "Tasks",
      dataIndex: "count",
      align: "right",
      width: 56,
    },
    {
      title: "Total %",
      dataIndex: "total",
      align: "right",
      width: 84,
      render: (_: unknown, t: TotalRow) => {
        const color =
          t.total > 100 ? "#f5222d" : t.total === 100 ? "#52c41a" : "#faad14";
        const note =
          t.total > 100 ? "Over 100%" : t.total < 100 ? "Under 100%" : null;
        return (
          <div>
            <div style={{ fontWeight: 600, color }}>{t.total}%</div>
            {note ? (
              <div
                style={{ fontSize: 10, color, wordBreak: "break-word" }}
              >
                {note}
              </div>
            ) : null}
          </div>
        );
      },
    },
  ];

  const content = (
    <div style={{ maxWidth: 380, maxHeight: 320, overflowY: "auto" }}>
      <Table<TotalRow>
        size="small"
        columns={columns}
        dataSource={totals}
        rowKey={(t) => `${t.phase}||${t.activity}`}
        pagination={false}
        tableLayout="fixed"
      />
      {truncated ? (
        <p style={{ margin: "8px 0 0", fontSize: 11, color: "#8094ae" }}>
          Totals cover the first {report.rows.length} of {report.totalRecords}{" "}
          rows shown.
        </p>
      ) : null}
      <p style={{ margin: "8px 0 0", fontSize: 11, color: "#8094ae" }}>
        Computed from the uploaded rows (excludes any tasks already saved under a
        reused activity).
      </p>
    </div>
  );

  return (
    <Popover
      placement="bottomLeft"
      title="Task weightage by activity"
      content={content}
    >
      <Tag color={anyUnder ? "gold" : "blue"} style={{ cursor: "pointer" }}>
        Activity totals ({totals.length}){anyUnder ? " · some < 100%" : ""}
      </Tag>
    </Popover>
  );
};

const WbsUploadModal = ({
  projectId,
  projectName,
  projectSiteName,
  projectSiteId,
  open,
  onClose,
  onSuccess,
}: {
  projectId?: string;
  projectName?: string;
  projectSiteName?: string;
  projectSiteId?: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) => {
  const gate = useSequenceGate({
    projectId,
    requiredModules: WBS_REQUIRED_MODULES,
    projectName,
    projectSiteName,
    projectSiteId,
  });

  const config: ExcelUploadTypeConfig = useMemo(
    () => ({
      title: "Bulk WBS Creation",
      entityLabel: "WBS rows",
      templateSource: { kind: "blob", url: WBS_TEMPLATE_URL },
      startUpload: (formData: FormData) => projectsApis.uploadWBS(formData),
      buildBody: (comments: string) => ({
        projectId,
        comment: comments ? comments : "N/A",
      }),
      previewColumns: wbsUploadPreviewColumns,
      previewScrollX: WBS_UPLOAD_TABLE_WIDTH,
      summaryTags: (report: UploadReportState) => (
        <WbsActivityTotals report={report} />
      ),
      // WBS preview rows carry no excelRow field (backend pick() list) — fall
      // back to array position; row n on page 1 = Excel row n+2.
      rowErrorLabel: (row: any, index: number) =>
        row?.taskName || `Row ${index + 2}`,
      copy: {
        uploadIntro: (
          <p>
            You're about to import a WBS (phases, activities, tasks and
            dependencies). Fill the WBS Excel template accurately and
            completely, then upload it below. You'll see a full preview before
            anything is saved.
          </p>
        ),
        validatingHeading: "Validating your file…",
        validatingSub:
          "Checking phases, activities, tasks, owners, UOMs and dependencies.",
        committingHeading: "Creating WBS…",
        committingSub:
          "Inserting phases, activities, tasks and dependency links.",
        commitCta: "Create WBS",
        doneHeading: "WBS imported successfully",
        doneSub: (report: UploadReportState) => (
          <>
            {report.successCount} WBS row(s) imported. The phases, activities
            and tasks are now visible in the list.
          </>
        ),
        creationFailedNote:
          "Creation failed — no WBS rows were saved (the batch is all-or-nothing).",
      },
      gate,
      introSection: (
        <WbsUploadIntro
          projectId={projectId}
          projectSiteName={projectSiteName}
          projectSiteId={projectSiteId}
        />
      ),
      discardOnCancelFromPreview: true,
    }),
    [projectId, projectSiteName, projectSiteId, gate]
  );

  return (
    <ExcelUploadStageModal
      config={config}
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      projectId={projectId}
    />
  );
};

export default WbsUploadModal;
